import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, XCircle, Loader, Save, Edit, X, Send, TestTube, RefreshCw, FileText, Copy, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { metaApiService } from '../../services/metaApiService';
import { ParametrosSistema, MetaApiConfig, WhatsAppTemplate, WhatsAppPhoneNumber } from '../../types/FireAgent';
import { useNotification } from '../../hooks/useNotification';

const FireAgentConfiguracoes: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showInfo, NotificationComponent } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingMetaApi, setTestingMetaApi] = useState(false);
  const [testMetaApiResult, setTestMetaApiResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isEditingMetaApi, setIsEditingMetaApi] = useState(false);
  const [originalMetaApiConfig, setOriginalMetaApiConfig] = useState<MetaApiConfig | null>(null);

  // Estados para teste de envio
  const [testeTelefone, setTesteTelefone] = useState('');
  const [testeNome, setTesteNome] = useState('');
  const [testeMensagem, setTesteMensagem] = useState('');
  const [testando, setTestando] = useState(false);
  const [testeTemplateName, setTesteTemplateName] = useState('');
  const [testeTemplateLanguage, setTesteTemplateLanguage] = useState('pt_BR');
  const [templatesDisponiveis, setTemplatesDisponiveis] = useState<WhatsAppTemplate[]>([]);
  const [carregandoTemplates, setCarregandoTemplates] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<WhatsAppPhoneNumber[]>([]);
  const [carregandoPhoneNumbers, setCarregandoPhoneNumbers] = useState(false);
  const [showPhoneNumbersModal, setShowPhoneNumbersModal] = useState(false);
  
  // Estados para registro On-Premises
  const [showOnPremisesModal, setShowOnPremisesModal] = useState(false);
  const [onPremisesCert, setOnPremisesCert] = useState('');
  const [onPremisesPhone, setOnPremisesPhone] = useState('');
  const [onPremisesCountryCode, setOnPremisesCountryCode] = useState('55');
  const [onPremisesMethod, setOnPremisesMethod] = useState<'sms' | 'voice'>('sms');
  const [onPremisesPin, setOnPremisesPin] = useState('');
  const [onPremisesCode, setOnPremisesCode] = useState('');
  const [registrandoOnPremises, setRegistrandoOnPremises] = useState(false);

  const [parametros, setParametros] = useState<ParametrosSistema>({
    diasAtrasoParaCobranca: 1,
    intervaloEntreCobrancas: 1,
    horarioEnvioInicio: '09:00',
    horarioEnvioFim: '18:00'
  });

  const [metaApiConfig, setMetaApiConfig] = useState<MetaApiConfig>({
    ativo: false,
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    templateName: '',
    templateLanguage: 'pt_BR',
    version: 'v24.0',
    apiType: 'cloud', // Padrão: Cloud API
    onPremisesBaseUrl: '',
    configuradoEm: '',
    configuradoPor: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Preencher nome do usuário quando carregar dados
  useEffect(() => {
    if (userProfile?.nome && !testeNome) {
      setTesteNome(userProfile.nome);
    }
  }, [userProfile]);

  // Carregar templates automaticamente quando a configuração estiver disponível
  useEffect(() => {
    if (metaApiConfig && metaApiConfig.accessToken && metaApiConfig.businessAccountId && !carregandoTemplates && templatesDisponiveis.length === 0) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaApiConfig?.accessToken, metaApiConfig?.businessAccountId]);

  const loadData = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);

      // Carregar parâmetros
      const savedParametros = await firebaseService.getParametrosSistema(userProfile.empresaCnpj);
      if (savedParametros) {
        setParametros(savedParametros);
      }

      // Carregar configuração Meta API
      const savedMetaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (savedMetaApiConfig && savedMetaApiConfig.configuradoEm) {
        setMetaApiConfig(savedMetaApiConfig);
        setOriginalMetaApiConfig(savedMetaApiConfig);
        metaApiService.setConfig(savedMetaApiConfig);
        setIsEditingMetaApi(false);
        
        // Carregar templates disponíveis automaticamente se houver configuração com Business Account ID
        if (savedMetaApiConfig.accessToken && savedMetaApiConfig.businessAccountId) {
          // Carregar templates diretamente, sem setTimeout
          try {
            setCarregandoTemplates(true);
            const templates = await metaApiService.listWhatsAppTemplates();
            setTemplatesDisponiveis(templates);
            console.log('Templates carregados:', templates);
          } catch (error: any) {
            console.error('Erro ao carregar templates:', error);
            setTemplatesDisponiveis([]);
          } finally {
            setCarregandoTemplates(false);
          }
        }
      } else {
        setIsEditingMetaApi(true);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showError('Erro', 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleParametrosChange = (field: keyof ParametrosSistema, value: any) => {
    setParametros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveParametros = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setSaving(true);
      await firebaseService.saveParametrosSistema(userProfile.empresaCnpj, parametros);
      showSuccess('Sucesso', 'Parâmetros salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar parâmetros:', error);
      showError('Erro', 'Erro ao salvar parâmetros');
    } finally {
      setSaving(false);
    }
  };

  const handleMetaApiConfigChange = (field: keyof MetaApiConfig, value: any) => {
    setMetaApiConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const loadTemplates = async () => {
    // Verificar se há configuração disponível (pode ser salva ou em edição)
    const configParaUsar = metaApiConfig && metaApiConfig.accessToken && metaApiConfig.businessAccountId 
      ? metaApiConfig 
      : null;

    if (!configParaUsar) {
      setTemplatesDisponiveis([]);
      return;
    }

    try {
      setCarregandoTemplates(true);
      // Garantir que o serviço está configurado
      metaApiService.setConfig(configParaUsar);
      const templates = await metaApiService.listWhatsAppTemplates();
      setTemplatesDisponiveis(templates);
      console.log('Templates carregados:', templates);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      setTemplatesDisponiveis([]);
    } finally {
      setCarregandoTemplates(false);
    }
  };

  const loadPhoneNumbers = async () => {
    if (!metaApiConfig.accessToken || !metaApiConfig.businessAccountId) {
      showInfo('Campos Obrigatórios', 'Preencha Access Token e Business Account ID para listar números de telefone');
      return;
    }

    try {
      setCarregandoPhoneNumbers(true);
      metaApiService.setConfig(metaApiConfig as MetaApiConfig);
      const numbers = await metaApiService.listPhoneNumbers();
      setPhoneNumbers(numbers);
      setShowPhoneNumbersModal(true);
    } catch (error: any) {
      showError('Erro', `Erro ao carregar números de telefone: ${error.message}`);
    } finally {
      setCarregandoPhoneNumbers(false);
    }
  };

  const handleSelectPhoneNumber = (phoneNumber: WhatsAppPhoneNumber) => {
    handleMetaApiConfigChange('phoneNumberId', phoneNumber.id);
    setShowPhoneNumbersModal(false);
    showSuccess('Sucesso', `Phone Number ID selecionado: ${phoneNumber.id}`);
  };

  // Função auxiliar para montar mensagem do template
  const montarMensagemTemplate = (templateName: string, nomeAluno: string): string => {
    const template = templatesDisponiveis.find(t => t.name === templateName);
    if (!template) return '';

    const nomeUsuario = userProfile?.nome || 'Usuário';
    let mensagemTemplate = '';
    
    // Processar components do template
    if (template.components && Array.isArray(template.components)) {
      template.components.forEach((component: any) => {
        if (component.type === 'HEADER' && component.text) {
          let headerText = component.text.replace(/\{\{1\}\}/g, nomeAluno);
          mensagemTemplate += headerText + '\n\n';
        } else if (component.type === 'BODY' && component.text) {
          let bodyText = component.text;
          // Para clientes_inativos, o {{1}} do body deve ser o nome do usuário logado
          if (templateName === 'clientes_inativos') {
            bodyText = bodyText.replace(/\{\{1\}\}/g, nomeUsuario); // {{1}} do body = nome do usuário logado
          } else {
            // Para outros templates, usar nomeAluno
            bodyText = bodyText.replace(/\{\{1\}\}/g, nomeAluno);
          }
          bodyText = bodyText.replace(/\{\{2\}\}/g, 'R$ 150,00');
          bodyText = bodyText.replace(/\{\{3\}\}/g, '5');
          bodyText = bodyText.replace(/\{\{4\}\}/g, new Date().toLocaleDateString('pt-BR'));
          mensagemTemplate += bodyText;
        } else if (component.type === 'FOOTER' && component.text) {
          mensagemTemplate += '\n\n' + component.text;
        }
      });
    } else {
      // Fallback: usar header, body, footer se components não estiver disponível
      if (template.header) {
        let headerText = template.header.replace(/\{\{1\}\}/g, nomeAluno);
        mensagemTemplate += headerText + '\n\n';
      }
      
      if (template.body) {
        let bodyText = template.body;
        // Para clientes_inativos, o {{1}} do body deve ser o nome do usuário logado
        if (templateName === 'clientes_inativos') {
          bodyText = bodyText.replace(/\{\{1\}\}/g, nomeUsuario); // {{1}} do body = nome do usuário logado
        } else {
          bodyText = bodyText.replace(/\{\{1\}\}/g, nomeAluno);
        }
        bodyText = bodyText.replace(/\{\{2\}\}/g, 'R$ 150,00')
          .replace(/\{\{3\}\}/g, '5')
          .replace(/\{\{4\}\}/g, new Date().toLocaleDateString('pt-BR'));
        mensagemTemplate += bodyText;
      }
      
      if (template.footer) {
        mensagemTemplate += '\n\n' + template.footer;
      }
    }
    
    return mensagemTemplate.trim();
  };

  const handleTemplateChange = (templateName: string) => {
    setTesteTemplateName(templateName);
    
    if (!templateName) {
      // Se não selecionou template, limpar mensagem
      setTesteMensagem('');
      return;
    }
    
    // Encontrar o template selecionado
    const template = templatesDisponiveis.find(t => t.name === templateName);
    
    if (template) {
      // Definir o idioma do template
      setTesteTemplateLanguage(template.language || 'pt_BR');
      
      // Usar o nome do teste ou o nome do usuário logado como padrão
      const nomeParaTemplate = testeNome || userProfile?.nome || '';
      
      // Montar a mensagem usando a função auxiliar
      const mensagemTemplate = montarMensagemTemplate(templateName, nomeParaTemplate);
      setTesteMensagem(mensagemTemplate);
    }
  };

  const handleEditMetaApi = () => {
    setOriginalMetaApiConfig({ ...metaApiConfig });
    setIsEditingMetaApi(true);
  };

  const handleCancelEditMetaApi = () => {
    if (originalMetaApiConfig) {
      setMetaApiConfig(originalMetaApiConfig);
    }
    setIsEditingMetaApi(false);
    setTestMetaApiResult(null);
  };

  const testMetaApiConnection = async () => {
    if (!metaApiConfig.accessToken || !metaApiConfig.phoneNumberId) {
      showInfo('Campos Obrigatórios', 'Preencha Access Token e Phone Number ID antes de testar');
      return;
    }

    try {
      setTestingMetaApi(true);
      setTestMetaApiResult(null);
      
      metaApiService.setConfig(metaApiConfig as MetaApiConfig);
      const success = await metaApiService.testConnection();
      
      if (success) {
        setTestMetaApiResult({ success: true, message: 'Conexão com Meta API estabelecida com sucesso!' });
      } else {
        setTestMetaApiResult({ success: false, message: 'Falha ao conectar com Meta API. Verifique as credenciais.' });
      }
    } catch (error: any) {
      setTestMetaApiResult({ success: false, message: `Erro: ${error.message}` });
    } finally {
      setTestingMetaApi(false);
    }
  };

  const handleSaveMetaApi = async () => {
    if (!userProfile?.empresaCnpj) return;

    if (!metaApiConfig.accessToken || !metaApiConfig.phoneNumberId) {
      showInfo('Campos Obrigatórios', 'Preencha Access Token e Phone Number ID');
      return;
    }

    try {
      setSaving(true);
      
      const configToSave: MetaApiConfig = {
        ...metaApiConfig,
        configuradoPor: userProfile.nome || userProfile.email,
        configuradoEm: metaApiConfig.configuradoEm || new Date().toISOString(),
        ativo: true,
        version: metaApiConfig.version || 'v24.0'
      };

      await firebaseService.saveMetaApiConfig(userProfile.empresaCnpj, configToSave);
      
      metaApiService.setConfig(configToSave);
      setOriginalMetaApiConfig(configToSave);
      setIsEditingMetaApi(false);
      
      showSuccess('Sucesso', 'Configuração Meta API salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração Meta API:', error);
      showError('Erro', 'Erro ao salvar configuração Meta API');
    } finally {
      setSaving(false);
    }
  };

  const handleTestarEnvio = async () => {
    if (!testeTemplateName) {
      showInfo('Template Necessário', 'Selecione um template antes de enviar a mensagem de teste.');
      return;
    }

    if (!testeTelefone.trim()) {
      showInfo('Campo Obrigatório', 'Digite um número de telefone');
      return;
    }

    if (!testeMensagem.trim()) {
      showInfo('Campo Obrigatório', 'A mensagem não pode estar vazia');
      return;
    }

    // Verificar se o Meta API está configurado
    if (!metaApiConfig || !metaApiConfig.accessToken || !metaApiConfig.phoneNumberId) {
      showInfo('Configuração Necessária', 'Configure o Meta API antes de testar');
      return;
    }

    try {
      setTestando(true);

      const nome = testeNome.trim() || 'Teste';
      const telefone = testeTelefone.trim().replace(/\D/g, ''); // Remove caracteres não numéricos
      const nomeUsuario = userProfile?.nome || 'Usuário';

      // Criar uma configuração temporária com o template do teste
      const configTemp = {
        ...metaApiConfig,
        templateName: testeTemplateName || undefined,
        templateLanguage: testeTemplateLanguage
      };
      
      // Configurar temporariamente o serviço com o template do teste
      metaApiService.setConfig(configTemp);
      
      // Para o template "clientes_inativos", construir parâmetros manualmente
      // HEADER: {{1}} = nome do aluno (campo "Nome")
      // BODY: {{1}} = nome do usuário logado
      let parametros: string[] = [];
      
      if (testeTemplateName === 'clientes_inativos') {
        // Template clientes_inativos tem 2 parâmetros:
        // 1. HEADER {{1}} = nome do aluno
        // 2. BODY {{1}} = nome do usuário logado
        parametros = [nome, nomeUsuario];
      } else {
        // Para outros templates, tentar extrair parâmetros da mensagem
        // ou buscar detalhes do template para saber quantos parâmetros são necessários
        const template = templatesDisponiveis.find(t => t.name === testeTemplateName);
        
        if (template && template.components && Array.isArray(template.components)) {
          // Contar variáveis no template
          let headerVars = 0;
          let bodyVars = 0;
          
          template.components.forEach((comp) => {
            if (comp.type === 'HEADER' && comp.text) {
              const matches = comp.text.match(/\{\{[^}]+\}\}/g);
              if (matches) headerVars = matches.length;
            } else if (comp.type === 'BODY' && comp.text) {
              const matches = comp.text.match(/\{\{[^}]+\}\}/g);
              if (matches) bodyVars = matches.length;
            }
          });
          
          // Construir parâmetros baseado nas variáveis encontradas
          if (headerVars > 0) {
            parametros.push(nome); // Primeiro parâmetro para HEADER
          }
          
          // Parâmetros para BODY
          for (let i = 0; i < bodyVars; i++) {
            if (i === 0) {
              parametros.push(nome); // Primeiro parâmetro do BODY geralmente é o nome
            } else {
              parametros.push('N/A'); // Outros parâmetros com valor padrão
            }
          }
        }
        // Se não encontrou template ou não tem variáveis, tentar sem parâmetros
      }
      
      // Enviar mensagem usando sendTemplateMessage diretamente
      // Se não houver parâmetros, o sendTemplateMessage tentará sem parâmetros
      const response = await metaApiService.sendTemplateMessage(
        telefone,
        testeTemplateName,
        parametros,
        testeTemplateLanguage
      );
      
      // Restaurar a configuração original
      metaApiService.setConfig(metaApiConfig);

      if (response.messages && response.messages.length > 0) {
        showSuccess('Sucesso', 'Mensagem enviada com sucesso!');
        // Limpar campos após sucesso
        setTesteTelefone('');
        setTesteNome('');
        setTesteMensagem('');
      } else {
        showError('Erro', 'Erro ao enviar mensagem: resposta inválida');
      }
    } catch (error: any) {
      showError('Erro', error.message || 'Erro ao enviar mensagem');
    } finally {
      setTestando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações FireAgent</h1>
          <p className="text-gray-600">Configure a integração com Meta API (WhatsApp Business API) para envio de mensagens</p>
        </div>
      </div>

      {/* Configuração do Meta API */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Configuração do Meta API (WhatsApp Business API)</h2>
          </div>
          {metaApiConfig.configuradoEm && !isEditingMetaApi && (
            <button
              onClick={handleEditMetaApi}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Editar
            </button>
          )}
          {isEditingMetaApi && originalMetaApiConfig && (
            <button
              onClick={handleCancelEditMetaApi}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
          )}
        </div>

        {metaApiConfig.configuradoEm && (
          <div className="mb-4 p-3 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-700">
                Configurado em {new Date(metaApiConfig.configuradoEm).toLocaleDateString('pt-BR')} por {metaApiConfig.configuradoPor}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Token *
            </label>
            <input
              type="password"
              value={metaApiConfig.accessToken}
              onChange={(e) => handleMetaApiConfigChange('accessToken', e.target.value)}
              placeholder="Seu token de acesso permanente do Meta"
              readOnly={!isEditingMetaApi}
              className={`input w-full ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            />
            <p className="text-xs text-gray-500 mt-1">Token de acesso permanente gerado no Meta Business Manager</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number ID *
              </label>
              <div className="flex gap-2">
              <input
                type="text"
                value={metaApiConfig.phoneNumberId}
                onChange={(e) => handleMetaApiConfigChange('phoneNumberId', e.target.value)}
                placeholder="ID do número de telefone"
                readOnly={!isEditingMetaApi}
                  className={`input flex-1 ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                />
                {isEditingMetaApi && (
                  <button
                    type="button"
                    onClick={loadPhoneNumbers}
                    disabled={carregandoPhoneNumbers || !metaApiConfig.accessToken || !metaApiConfig.businessAccountId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Listar números de telefone disponíveis"
                  >
                    {carregandoPhoneNumbers ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">Listar</span>
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ID do número de telefone do WhatsApp Business (apenas números, ex: 123456789012345). 
                <br />
                <strong>NÃO</strong> use o número de telefone formatado (ex: +553898178673). 
                <br />
                {isEditingMetaApi && (
                  <>
                    Clique em "Listar" para ver os números disponíveis ou encontre o ID em: Meta Business Manager → WhatsApp → API Setup → Phone numbers
                    <br />
                    <button
                      type="button"
                      onClick={() => setShowOnPremisesModal(true)}
                      className="text-blue-600 hover:text-blue-700 underline text-xs mt-1"
                    >
                      Número está pendente? Clique aqui para registro via API On-Premises
                    </button>
                  </>
                )}
                {!isEditingMetaApi && (
                  <>
                    Encontre o ID em: Meta Business Manager → WhatsApp → API Setup → Phone numbers
                  </>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Account ID (WABA ID) <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 font-normal ml-2">(obrigatório para listar templates)</span>
              </label>
              <input
                type="text"
                value={metaApiConfig.businessAccountId || ''}
                onChange={(e) => handleMetaApiConfigChange('businessAccountId', e.target.value)}
                placeholder="Ex: 123456789012345"
                readOnly={!isEditingMetaApi}
                className={`input w-full ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Meta Business Suite → Configurações → Contas do WhatsApp → Selecione sua conta → O ID aparece no topo da página
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Sem este ID, você poderá enviar mensagens, mas não conseguirá listar templates na página de disparo.
              </p>
            </div>
          </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de API
            </label>
            <select
              value={metaApiConfig.apiType || 'cloud'}
              onChange={(e) => handleMetaApiConfigChange('apiType', e.target.value as 'cloud' | 'on-premises')}
              disabled={!isEditingMetaApi}
              className={`input w-full ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            >
              <option value="cloud">Cloud API (Graph API) - Padrão</option>
              <option value="on-premises">On-Premises API</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {metaApiConfig.apiType === 'cloud' 
                ? 'Usa a Cloud API do Meta (graph.facebook.com) - Recomendado'
                : 'Usa a API On-Premises - Requer servidor próprio configurado'}
            </p>
          </div>

          {metaApiConfig.apiType === 'on-premises' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL Base do Servidor On-Premises <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={metaApiConfig.onPremisesBaseUrl || ''}
                onChange={(e) => handleMetaApiConfigChange('onPremisesBaseUrl', e.target.value)}
                placeholder="https://seu-servidor/v1"
                readOnly={!isEditingMetaApi}
                className={`input w-full ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                URL base do seu servidor On-Premises (ex: https://seu-servidor/v1)
              </p>
            </div>
          )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Versão da API
              </label>
              <input
                type="text"
              value={metaApiConfig.version || 'v24.0'}
                onChange={(e) => handleMetaApiConfigChange('version', e.target.value)}
              placeholder="v24.0"
                readOnly={!isEditingMetaApi}
                className={`input w-full ${!isEditingMetaApi ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              />
            <p className="text-xs text-gray-500 mt-1">
              Versão da API do Meta (padrão: v24.0) - Apenas para Cloud API
            </p>
          </div>

          {/* Teste de Conexão Meta API */}
          <div className="flex gap-2 pt-4 border-t">
            <button
              onClick={testMetaApiConnection}
              disabled={!isEditingMetaApi || testingMetaApi || !metaApiConfig.accessToken || !metaApiConfig.phoneNumberId}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <TestTube className="h-4 w-4" />
              {testingMetaApi ? 'Testando...' : 'Testar Conexão'}
            </button>

            {testMetaApiResult && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                testMetaApiResult.success ? 'text-green-600' : 'text-red-600'
              }`}>
                {testMetaApiResult.success ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <span className="text-sm">{testMetaApiResult.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* Botão Salvar Meta API */}
        {isEditingMetaApi && (
          <div className="flex justify-end mt-4 pt-4 border-t">
            <button
              onClick={handleSaveMetaApi}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Salvando...' : 'Salvar Configuração Meta API'}
            </button>
          </div>
        )}
      </div>

      {/* Parâmetros do Sistema */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Parâmetros do Sistema</h2>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dias de Atraso para Cobrança *
              </label>
              <input
                type="number"
                value={parametros.diasAtrasoParaCobranca}
                onChange={(e) => handleParametrosChange('diasAtrasoParaCobranca', parseInt(e.target.value) || 1)}
                min="1"
                className="input w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Número de dias após vencimento para considerar em atraso</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intervalo entre Cobranças (dias)
              </label>
              <input
                type="number"
                value={parametros.intervaloEntreCobrancas}
                onChange={(e) => handleParametrosChange('intervaloEntreCobrancas', parseInt(e.target.value) || 1)}
                min="1"
                className="input w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Dias de espera entre envios de cobrança</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horário de Envio - Início
              </label>
              <input
                type="time"
                value={parametros.horarioEnvioInicio}
                onChange={(e) => handleParametrosChange('horarioEnvioInicio', e.target.value)}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horário de Envio - Fim
              </label>
              <input
                type="time"
                value={parametros.horarioEnvioFim}
                onChange={(e) => handleParametrosChange('horarioEnvioFim', e.target.value)}
                className="input w-full"
              />
        </div>
      </div>

          {/* Botão Salvar Parâmetros */}
          <div className="flex justify-end pt-4 border-t">
          <button
              onClick={handleSaveParametros}
            disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
              {saving ? 'Salvando...' : 'Salvar Parâmetros'}
          </button>
        </div>
        </div>
      </div>

      {/* Seção de Teste de Envio */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Teste de Envio</h2>
            <p className="text-sm text-gray-600">Envie uma mensagem de teste para qualquer número antes de disparar para os clientes</p>
          </div>
        </div>

        {/* Template - DEVE VIR PRIMEIRO */}
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Template *</h3>
              <p className="text-xs text-gray-500 mt-1">
                Selecione um template aprovado. A mensagem será preenchida automaticamente.
              </p>
            </div>
            <button
              onClick={loadTemplates}
              disabled={carregandoTemplates || !metaApiConfig.ativo}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
              title="Recarregar templates"
            >
              <RefreshCw className={`h-3 w-3 ${carregandoTemplates ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
          
          {carregandoTemplates ? (
            <div className="input w-full flex items-center gap-2 text-gray-500">
              <Loader className="h-4 w-4 animate-spin" />
              Carregando templates...
            </div>
          ) : (
            <select
              value={testeTemplateName}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="input w-full"
              required
            >
              <option value="">-- Selecione um template --</option>
              {templatesDisponiveis.map((template) => (
                <option key={template.id || template.name} value={template.name}>
                  {template.name} ({template.language || 'pt_BR'})
                </option>
              ))}
            </select>
          )}
          {templatesDisponiveis.length === 0 && !carregandoTemplates && (
            <p className="text-xs text-gray-500 mt-2">
              Nenhum template encontrado. Verifique se o Business Account ID está configurado.
            </p>
          )}
        </div>

        {/* Nome e Telefone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome
            </label>
            <input
              type="text"
              value={testeNome}
              onChange={(e) => {
                const novoNome = e.target.value;
                setTesteNome(novoNome);
                // Se houver template selecionado, remontar a mensagem com o novo nome
                // A função montarMensagemTemplate já garante que o body do clientes_inativos usa o nome do usuário logado
                if (testeTemplateName) {
                  const mensagemTemplate = montarMensagemTemplate(testeTemplateName, novoNome || userProfile?.nome || '');
                  setTesteMensagem(mensagemTemplate);
                }
              }}
              placeholder="Nome do remetente"
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefone *
            </label>
            <input
              type="text"
              value={testeTelefone}
              onChange={(e) => setTesteTelefone(e.target.value)}
              placeholder="(11) 99999-9999 ou 11999999999"
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Aceita qualquer formato. Será convertido automaticamente para E.164 (+5511999999999)
            </p>
          </div>
        </div>

        {/* Mensagem - SOMENTE LEITURA */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mensagem de Teste *
          </label>
          <textarea
            value={testeMensagem}
            readOnly
            rows={10}
            disabled={!testeTemplateName}
            className={`input w-full min-h-[200px] bg-gray-50 cursor-default ${!testeTemplateName ? 'opacity-50' : ''}`}
            placeholder={testeTemplateName ? "A mensagem será preenchida automaticamente com base no template selecionado" : "Selecione um template acima para preencher a mensagem"}
          />
          {!testeTemplateName && (
            <p className="text-xs text-gray-500 mt-1">
              Selecione um template acima para visualizar a mensagem.
            </p>
          )}
          {testeTemplateName && (
            <p className="text-xs text-gray-500 mt-1">
              A mensagem é gerada automaticamente. Altere o nome acima para atualizar o header.
            </p>
          )}
        </div>


        <button
          onClick={handleTestarEnvio}
          disabled={testando || !testeTemplateName || !testeTelefone.trim() || !testeMensagem.trim() || !metaApiConfig.ativo}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testando ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Enviando teste...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Enviar Mensagem de Teste
            </>
          )}
        </button>
      </div>

      {/* Modal de Seleção de Números de Telefone */}
      {showPhoneNumbersModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowPhoneNumbersModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Números de Telefone Disponíveis
              </h3>
              <button
                onClick={() => setShowPhoneNumbersModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              {phoneNumbers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum número de telefone encontrado.
                </div>
              ) : (
                <div className="space-y-3">
                  {phoneNumbers.map((phone) => (
                    <div
                      key={phone.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleSelectPhoneNumber(phone)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">
                              {phone.verified_name || 'Sem nome verificado'}
                            </span>
                            {phone.quality_rating && (
                              <span className={`px-2 py-1 text-xs rounded ${
                                phone.quality_rating === 'GREEN' 
                                  ? 'bg-green-100 text-green-800' 
                                  : phone.quality_rating === 'YELLOW'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {phone.quality_rating}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            <strong>Número:</strong> {phone.display_phone_number}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            <strong>Phone Number ID:</strong> 
                            <span className="font-mono bg-gray-100 px-2 py-1 rounded ml-2">
                              {phone.id}
                            </span>
                          </div>
                          {(() => {
                            const accountStatus = phone.account_status || phone.status;
                            const codeVerificationStatus = phone.code_verification_status;
                            const isConnected = accountStatus === 'CONNECTED' || accountStatus === 'connected';
                            const isVerified = codeVerificationStatus === 'VERIFIED';
                            const isUnverified = codeVerificationStatus === 'UNVERIFIED' || codeVerificationStatus === 'NOT_VERIFIED';
                            
                            // Se o número está CONECTADO no Meta, considerar como verificado mesmo se code_verification_status não estiver VERIFIED
                            if (isConnected && !isUnverified) {
                              return (
                                <div className="text-xs mt-2 px-2 py-1 rounded bg-green-100 text-green-800">
                                  <div className="flex items-center gap-1 mb-1">
                                    <span>✓</span>
                                    <strong>Status: Conectado e Funcional</strong>
                                  </div>
                                  {accountStatus && (
                                    <p className="text-xs">
                                      Account Status: {accountStatus}
                                    </p>
                                  )}
                                  {codeVerificationStatus && codeVerificationStatus !== 'VERIFIED' && (
                                    <p className="text-xs mt-1 italic">
                                      Nota: O número está conectado no Meta Business Manager, mesmo que o status de verificação de código não esteja como VERIFIED.
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            
                            // Se tem code_verification_status, mostrar
                            if (codeVerificationStatus) {
                              return (
                                <div className={`text-xs mt-2 px-2 py-1 rounded ${
                                  isVerified
                                    ? 'bg-green-100 text-green-800'
                                    : isUnverified
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  <strong>Status de Verificação:</strong> {codeVerificationStatus}
                                  {accountStatus && (
                                    <p className="text-xs mt-1">
                                      Account Status: {accountStatus}
                                    </p>
                                  )}
                                  {isUnverified && (
                                    <div className="mt-1 text-xs">
                                      ⚠️ Este número precisa completar a verificação. Acesse o Meta Business Manager para verificar o código recebido via SMS.
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            // Se não tem nenhum status, mostrar aviso
                            return (
                              <div className="text-xs text-yellow-600 mt-2 px-2 py-1 rounded bg-yellow-50">
                                ⚠️ Status de verificação não disponível. Verifique no Meta Business Manager se o número está completamente registrado.
                                {accountStatus && (
                                  <p className="text-xs mt-1">
                                    Account Status: {accountStatus}
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                          {/* Aviso sobre números pendentes */}
                          <div className="text-xs text-orange-600 mt-2 px-2 py-1 rounded bg-orange-50 border border-orange-200">
                            <strong>⚠️ IMPORTANTE:</strong> Se o número aparece como "Pendente" no Meta Business Manager, ele ainda não está completamente registrado e não pode enviar mensagens. Aguarde a aprovação ou complete o processo de registro.
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPhoneNumber(phone);
                          }}
                          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Selecionar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowPhoneNumbersModal(false)}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Registro On-Premises */}
      {showOnPremisesModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowOnPremisesModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Registro via API On-Premises
              </h3>
              <button
                onClick={() => setShowOnPremisesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Atenção:</strong> A API On-Premises está sendo descontinuada pela Meta. 
                  Este processo requer um servidor On-Premises configurado e um certificado baixado do Meta Business Manager.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código do País (CC)
                  </label>
                  <input
                    type="text"
                    value={onPremisesCountryCode}
                    onChange={(e) => setOnPremisesCountryCode(e.target.value)}
                    placeholder="55"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Telefone
                  </label>
                  <input
                    type="text"
                    value={onPremisesPhone}
                    onChange={(e) => setOnPremisesPhone(e.target.value)}
                    placeholder="11987654321"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Método de Verificação
                  </label>
                  <select
                    value={onPremisesMethod}
                    onChange={(e) => setOnPremisesMethod(e.target.value as 'sms' | 'voice')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="sms">SMS</option>
                    <option value="voice">Voice (Ligação)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificado (Base64) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={onPremisesCert}
                    onChange={(e) => {
                      console.log('📜 Certificado alterado:', e.target.value ? 'Preenchido (' + e.target.value.length + ' caracteres)' : 'Vazio');
                      setOnPremisesCert(e.target.value);
                    }}
                    placeholder="Cole aqui o certificado em Base64 baixado do Meta Business Manager"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Baixe o certificado em: Meta Business Manager → WhatsApp → API Setup → Baixar Certificado
                  </p>
                  {!onPremisesCert && (
                    <p className="text-xs text-red-500 mt-1">Campo obrigatório</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PIN (Opcional)
                  </label>
                  <input
                    type="text"
                    value={onPremisesPin}
                    onChange={(e) => setOnPremisesPin(e.target.value)}
                    placeholder="PIN do certificado (se houver)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {onPremisesCode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código de Verificação Recebido
                    </label>
                    <input
                      type="text"
                      value={onPremisesCode}
                      onChange={(e) => setOnPremisesCode(e.target.value)}
                      placeholder="Digite o código recebido via SMS/Voice"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>📋 Instruções:</strong>
                  <br />
                  1. Baixe o certificado do Meta Business Manager
                  <br />
                  2. Converta o certificado para Base64 (use um conversor online)
                  <br />
                  3. Preencha os campos acima
                  <br />
                  4. Clique em "Registrar" para iniciar o processo
                  <br />
                  5. Aguarde o código de verificação via SMS ou Voice
                  <br />
                  6. Digite o código recebido e clique em "Verificar"
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  console.log('🔵 Botão Registrar clicado');
                  console.log('📱 Telefone:', onPremisesPhone);
                  console.log('📜 Certificado:', onPremisesCert ? 'Preenchido' : 'Vazio');
                  
                  if (!onPremisesPhone || !onPremisesCert) {
                    console.log('❌ Validação falhou - campos vazios');
                    showError('Erro', 'Preencha o número de telefone e o certificado');
                    return;
                  }
                  
                  console.log('✅ Validação passou, iniciando registro...');
                  
                  try {
                    setRegistrandoOnPremises(true);
                    console.log('🔄 Chamando registerPhoneNumberOnPremises...');
                    
                    const result = await metaApiService.registerPhoneNumberOnPremises({
                      cc: onPremisesCountryCode,
                      phone_number: onPremisesPhone,
                      method: onPremisesMethod,
                      cert: onPremisesCert,
                      pin: onPremisesPin || undefined
                    });
                    
                    console.log('📦 Resultado recebido:', result);
                    
                    // A função retorna OnPremisesRegistrationResponse
                    if (result.account && result.account.length > 0) {
                      showSuccess('Sucesso', 'Registro iniciado! Verifique seu telefone para o código de verificação.');
                    } else if (result.error) {
                      showError('Erro', result.error.message || 'Erro ao registrar número');
                    } else {
                      showInfo('Info', 'Processo iniciado. Verifique seu telefone para o código de verificação.');
                    }
                  } catch (error: any) {
                    console.error('❌ Erro capturado:', error);
                    // A função atual sempre lança um erro informativo sobre a necessidade de servidor On-Premises
                    // Mostrar o erro de forma mais amigável
                    const errorMessage = error.message || 'Erro ao registrar número';
                    if (errorMessage.includes('servidor próprio')) {
                      showError(
                        'API On-Premises',
                        '⚠️ API On-Premises requer servidor próprio\n\n' +
                        'A função de registro On-Premises requer um servidor On-Premises configurado.\n\n' +
                        'Por enquanto, esta funcionalidade está disponível apenas para orientação.\n\n' +
                        'Para usar:\n' +
                        '1. Configure um servidor On-Premises\n' +
                        '2. Ou complete o registro via Meta Business Manager (interface web)'
                      );
                    } else {
                      showError('Erro', errorMessage);
                    }
                  } finally {
                    setRegistrandoOnPremises(false);
                    console.log('🏁 Processo finalizado');
                  }
                }}
                disabled={registrandoOnPremises || !onPremisesPhone || !onPremisesCert}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                title={
                  !onPremisesPhone || !onPremisesCert
                    ? 'Preencha o número de telefone e o certificado para continuar'
                    : registrandoOnPremises
                    ? 'Registrando...'
                    : 'Clique para registrar o número via API On-Premises'
                }
              >
                {registrandoOnPremises ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  'Registrar'
                )}
              </button>

              {onPremisesCode && (
                <button
                  onClick={async () => {
                    try {
                      setRegistrandoOnPremises(true);
                      const result = await metaApiService.verifyPhoneNumberOnPremises({
                        cc: onPremisesCountryCode,
                        phone_number: onPremisesPhone,
                        code: onPremisesCode
                      });
                      
                      if (result.success) {
                        showSuccess('Sucesso', 'Número verificado com sucesso!');
                        setShowOnPremisesModal(false);
                        // Recarregar números de telefone
                        await loadPhoneNumbers();
                      } else {
                        showError('Erro', result.message || 'Erro ao verificar código');
                      }
                    } catch (error: any) {
                      showError('Erro', error.message || 'Erro ao verificar código');
                    } finally {
                      setRegistrandoOnPremises(false);
                    }
                  }}
                  disabled={registrandoOnPremises || !onPremisesCode}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {registrandoOnPremises ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    'Verificar Código'
                  )}
                </button>
              )}

              <button
                onClick={() => setShowOnPremisesModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationComponent />
    </div>
  );
};

export default FireAgentConfiguracoes;

